import { useState } from 'react';
import { useKeybindingStats } from '../hooks/keybinding/useKeybinding.js';
import { formatKeyDisplay } from '../hooks/keybinding/utils.js';

/**
 * KeybindingDebugger - Development tool for debugging keybindings
 * 
 * Shows real-time information about:
 * - Current keybinding state
 * - Recent key presses
 * - Registered bindings
 * - Execution statistics
 * - Registry export/import
 */
const KeybindingDebugger = () => {
  const stats = useKeybindingStats();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState('state');
  
  // Only show in development (temporarily disabled for user testing)
  if (!import.meta.env.DEV || true) {
    return null;
  }
  
  const exportData = () => {
    const data = stats.export();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keybinding-debug-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const copyToClipboard = () => {
    const data = stats.export();
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };
  
  if (!isExpanded) {
    return (
      <div className="fixed top-spacing-4 left-spacing-4 z-[9999]">
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-bg-tooltip border border-border-default rounded px-spacing-2 py-spacing-1 text-xs font-mono text-secondary hover:text-primary transition-colors"
          title="Open Keybinding Debugger"
        >
          KB Debug
        </button>
      </div>
    );
  }
  
  return (
    <div className="fixed top-spacing-4 left-spacing-4 z-[9999] bg-bg-tooltip border border-border-default rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-spacing-2 border-b border-border-divider">
        <h3 className="text-sm font-normal text-primary">Keybinding Debugger</h3>
        <div className="flex items-center gap-spacing-1">
          <button
            onClick={copyToClipboard}
            className="text-xs text-secondary hover:text-primary"
            title="Copy debug data"
          >
            📋
          </button>
          <button
            onClick={exportData}
            className="text-xs text-secondary hover:text-primary"
            title="Export debug data"
          >
            💾
          </button>
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-secondary hover:text-primary"
            title="Close debugger"
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-border-divider">
        {[
          { id: 'state', label: 'State' },
          { id: 'bindings', label: 'Bindings' },
          { id: 'history', label: 'History' },
          { id: 'stats', label: 'Stats' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`px-spacing-2 py-spacing-1 text-xs font-normal transition-colors ${
              selectedTab === tab.id
                ? 'text-primary bg-bg-hover'
                : 'text-secondary hover:text-primary hover:bg-bg-panel'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="p-spacing-2 overflow-y-auto max-h-64 text-xs font-mono">
        {selectedTab === 'state' && (
          <StateTab stats={stats} />
        )}
        
        {selectedTab === 'bindings' && (
          <BindingsTab stats={stats} />
        )}
        
        {selectedTab === 'history' && (
          <HistoryTab stats={stats} />
        )}
        
        {selectedTab === 'stats' && (
          <StatsTab stats={stats} />
        )}
      </div>
    </div>
  );
};

const StateTab = ({ stats }) => {
  const data = stats.export().state;
  
  return (
    <div className="space-y-spacing-2">
      <div>
        <span className="text-secondary">Building:</span>{' '}
        <span className={data.isBuilding ? 'text-success' : 'text-secondary'}>
          {data.isBuilding ? 'Yes' : 'No'}
        </span>
      </div>
      
      <div>
        <span className="text-secondary">Enabled:</span>{' '}
        <span className={data.enabled ? 'text-success' : 'text-error'}>
          {data.enabled ? 'Yes' : 'No'}
        </span>
      </div>
      
      <div>
        <span className="text-secondary">Sequence:</span>{' '}
        <span className="text-primary">
          {data.currentSequence.length > 0 
            ? data.currentSequence.map(formatKeyDisplay).join(' → ') 
            : 'None'
          }
        </span>
      </div>
      
      <div>
        <span className="text-secondary">History Size:</span>{' '}
        <span className="text-primary">{data.history.length}</span>
      </div>
    </div>
  );
};

const BindingsTab = ({ stats }) => {
  const bindings = stats.allBindings.slice(0, 10); // Show first 10
  
  return (
    <div className="space-y-spacing-1">
      {bindings.length === 0 ? (
        <div className="text-secondary">No bindings registered</div>
      ) : (
        bindings.map((binding, index) => (
          <div key={index} className="border-b border-border-divider pb-spacing-1 last:border-b-0">
            <div className="text-primary">
              {binding.sequence} → {binding.action}
            </div>
            {binding.description && (
              <div className="text-secondary text-[10px]">
                {binding.description}
              </div>
            )}
            <div className="text-secondary text-[10px]">
              Scope: {binding.scope}
            </div>
          </div>
        ))
      )}
      
      {stats.allBindings.length > 10 && (
        <div className="text-secondary text-center">
          ... and {stats.allBindings.length - 10} more
        </div>
      )}
    </div>
  );
};

const HistoryTab = ({ stats }) => {
  const history = stats.history.slice(-10).reverse(); // Last 10, newest first
  
  return (
    <div className="space-y-spacing-1">
      {history.length === 0 ? (
        <div className="text-secondary">No execution history</div>
      ) : (
        history.map((entry, index) => (
          <div key={index} className="border-b border-border-divider pb-spacing-1 last:border-b-0">
            <div className="flex items-center justify-between">
              <span className="text-primary">
                {entry.sequence}
              </span>
              <span className={entry.success ? 'text-success' : 'text-error'}>
                {entry.success ? '✓' : '✗'}
              </span>
            </div>
            <div className="text-secondary text-[10px]">
              {entry.actionName}
            </div>
            <div className="text-secondary text-[10px]">
              {new Date(entry.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const StatsTab = ({ stats }) => {
  const registryStats = stats.registryStats;
  const executorStats = stats.executorStats;
  
  return (
    <div className="space-y-spacing-3">
      <div>
        <div className="text-secondary font-semibold mb-spacing-1">Registry</div>
        <div>Total: {registryStats.total}</div>
        <div>Active: {registryStats.active}</div>
        <div>Multi-key: {registryStats.multiKey}</div>
        <div>Prefixes: {registryStats.prefixes}</div>
      </div>
      
      <div>
        <div className="text-secondary font-semibold mb-spacing-1">Executor</div>
        <div>Actions: {executorStats.totalActions}</div>
        <div>Executions: {executorStats.totalExecutions}</div>
        <div>Middlewares: {executorStats.totalMiddlewares}</div>
        <div>Success Rate: {(executorStats.recentSuccessRate * 100).toFixed(1)}%</div>
      </div>
    </div>
  );
};

export default KeybindingDebugger;